import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PROPERTIES, PROPERTY_IMAGES } from '../utils/constants';
import ScrollReveal from './ScrollReveal';

const PropertyCard = ({ property, index, isReverse }) => {
  const navigate = useNavigate();
  
  const getPropertyImage = () => {
    if (property.id === 'jalsa-retreat') return PROPERTY_IMAGES.jalsaRetreat.exterior;
    if (property.id === 'jalsa-inn') return PROPERTY_IMAGES.jalsaInn.hero;
    return PROPERTY_IMAGES.hotelJalsa.hero;
  };

  return (
    <ScrollReveal direction={isReverse ? 'left' : 'right'} delay={index * 0.2}>
      <div className={`grid md:grid-cols-2 gap-8 items-center mb-32 ${
        isReverse ? 'md:grid-flow-dense' : ''
      }`}>
        {/* Image Section */}
        <motion.div 
          className={`relative group ${isReverse ? 'md:col-start-2' : ''}`}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <div className="relative overflow-hidden rounded-lg">
            {/* Main Image */}
            <img
              src={getPropertyImage()}
              alt={property.name}
              className="w-full h-[500px] object-cover transform group-hover:scale-110 transition-transform duration-700"
            />
            
            {/* Gold Overlay on Hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-gold-400/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            {/* Rating Badge */}
            <div className="absolute top-6 right-6 bg-navy-900/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center space-x-2">
              <Star className="w-4 h-4 text-gold-400 fill-gold-400" />
              <span className="text-white font-semibold">{property.rating}</span>
              <span className="text-gray-300 text-sm">({property.reviews})</span>
            </div>
            
            {/* Decorative Corner */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-gold-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-gold-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        </motion.div>

        {/* Content Section */}
        <div className={`space-y-6 ${isReverse ? 'md:col-start-1 md:row-start-1' : ''}`}>
          {/* Label */}
          <div className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-gold-400" />
            <span className="text-gold-400 text-sm tracking-widest uppercase font-light">
              {property.priceRange === '₹₹₹₹' ? 'Flagship Property' : 
               property.priceRange === '₹₹' ? 'Comfort Stay' : 'Business Hotel'}
            </span>
          </div>

          {/* Property Name */}
          <h2 className="text-4xl md:text-5xl font-serif text-navy-900">
            {property.name}
          </h2>

          {/* Tagline */}
          <p className="text-xl text-gold-500 font-light italic">
            "{property.tagline}"
          </p>

          {/* Decorative Divider */}
          <div className="flex items-center space-x-4 py-2">
            <div className="h-px w-12 bg-gold-400" />
            <div className="w-1.5 h-1.5 bg-gold-400 rotate-45" />
            <div className="h-px w-12 bg-gold-400" />
          </div>

          {/* Description */}
          <p className="text-gray-600 text-lg leading-relaxed">
            {property.description}
          </p>

          {/* Location */}
          <div className="flex items-center space-x-2 text-gray-700">
            <MapPin className="w-5 h-5 text-gold-500" />
            <span className="font-light">{property.location}</span>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 py-4">
            {property.features.slice(0, 4).map((feature, idx) => (
              <motion.div 
                key={idx} 
                className="flex items-center space-x-2"
                whileHover={{ x: 5 }}
              >
                <div className="w-1.5 h-1.5 bg-gold-400 rotate-45" />
                <span className="text-sm text-gray-700">{feature}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-4">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/properties/${property.id}`)}
              className="group flex items-center space-x-2 px-8 py-3 bg-navy-900 text-white hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider"
            >
              <span>EXPLORE</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>
            
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/properties/${property.id}`)}
              className="px-8 py-3 border-2 border-gold-400 text-gold-600 hover:bg-gold-400 hover:text-navy-900 transition-all duration-300 font-semibold tracking-wider"
            >
              BOOK NOW
            </motion.button>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

const PropertiesSection = () => {
  return (
    <section id="properties" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <ScrollReveal direction="up">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-400" />
              <div className="mx-4 w-2 h-2 bg-gold-400 rotate-45" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-400" />
            </div>
            
            <h2 className="text-5xl md:text-6xl font-serif text-navy-900 mb-6">
              Our Properties
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Discover three unique destinations, each offering its own blend of luxury, 
              comfort, and exceptional hospitality in the heart of Bhopal.
            </p>
          </div>
        </ScrollReveal>

        {/* Properties List */}
        <div className="max-w-7xl mx-auto">
          {PROPERTIES.map((property, index) => (
            <PropertyCard
              key={property.id}
              property={property}
              index={index}
              isReverse={index % 2 !== 0}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PropertiesSection;
